import { type MouseEvent, useCallback } from 'react'
import { InputLabel, Stack, ToggleButton, ToggleButtonGroup, styled } from '@mui/material'
import useId from '@mui/utils/useId'
import { ViewList as ViewListIcon, ViewModule as ViewModuleIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from '../../Common/_hooks/useAppDispatch'
import { useAppSelector } from '../../Common/_hooks/useAppSelector'
import { SaveUserPrefs } from '../../UserData/_actions/UserDataActions'
import type { ProductLayoutType } from '../../UserData/_stores/UserPrefsStore'

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, 0.06)',
  borderRadius: 30,
  padding: 4,
  width: '100%',

  '.MuiToggleButtonGroup-grouped': {
    border: 0,
    borderRadius: 24,
    margin: 0,

    '&:not(:first-of-type)': {
      border: 0,
      margin: 0,
    },
  },

  '.MuiToggleButton-root': {
    flex: 1,
    gap: theme.spacing(1),
    height: 36,
    textTransform: 'none',
  },
}))

interface Props {
  label: string
  value: ProductLayoutType
  onChange: (value: ProductLayoutType) => void
}

export default function AccountLayoutFilter(props: Props) {
  const dispatch = useAppDispatch()
  const [t] = useTranslation('account-v2')
  const id = useId()
  const userLayout = useAppSelector(state => state.get('userData').get('prefs').get('accountProductsLayout'))

  const onLayoutChange = useCallback((e: MouseEvent<HTMLElement>, value?: ProductLayoutType) => {
    if (!value) return

    props.onChange(value)

    if (value === userLayout) {
      return
    }

    const fd = new FormData()
    fd.append('accountProductsLayout', value)
    dispatch(SaveUserPrefs(fd, true))
  }, [dispatch, props.onChange, userLayout])

  return <Stack>
    <InputLabel htmlFor={id}>{props.label}</InputLabel>
    <StyledToggleButtonGroup
      id={id}
      exclusive
      value={props.value}
      onChange={onLayoutChange}
    >
      <ToggleButton value="grid">
        <ViewModuleIcon fontSize="small" />
        {t('Grid')}
      </ToggleButton>
      <ToggleButton value="list">
        <ViewListIcon fontSize="small" />
        {t('List')}
      </ToggleButton>
    </StyledToggleButtonGroup>
  </Stack>
}
